import type { State } from '../configureStore'
import { Auth as ActionTypes } from '../actionTypes'
import { defaultState } from '../reducers/auth/default.state'
import * as ReduxHelpers from '@src/utils/reduxHelpers'
import * as tenantproto from '@src/endpoints/services/tenantMgr.service'
import type {
  TenantInfo,
  OnboardingProgress,
  UserInfo,
  NotificationInfo
} from '@src/endpoints/services/commonHdr.service'
import {
  GetProgressReq,
  Calendar
} from '@src/endpoints/services/calendar.service'
import { GetProgressResp } from '@src/endpoints/services/commonHdr.service'
import { toastError, jsonify } from '@src/utils/toast'
import { correctEmailFormat } from '@src/utils/accounts'
import { AppStorage } from '@src/utils/storage.helpers'
import { storagePrefix } from '@src/constants/constants'
import AuthService from '@src/modules/oauth/services/auth.service'
import { defaultHandleStatus } from '@src/endpoints/helpers'
import { isEmpty } from 'lodash'
import {
  mapOrganizations,
  mapSingleAccounts
} from '../reducers/auth/helpers/organization.helper'
import type { Organizations, SingleAccounts } from '../reducers/auth/models'
import { SnapshotStateEnum } from '../reducers/auth/models'
import {
  pendoIdentify,
  pendoInitialize,
  pendoUpdateOptions
} from '@src/analytics/pendo'
import { differenceInMinutes } from 'date-fns'
import { getToday } from '@src/utils/date.helpers'
import { getFeatureFlagAsync } from '@src/endpoints/feature.flag'

//
// Action Creators
//

const storage = new AppStorage(storagePrefix, sessionStorage)

export type ActionTypes =
  | getStatSuccessAction
  | getTenantSuccessAction
  | getUserSuccessAction
  | getNotificationSuccessAction
  | serverErrorsAction
  | signOutSuccessAction
  | requestPendingAction
  | accountRegisterPendingAction
  | loggedInAction
  | requestPendingProgressAction
  | getProgressAction
  | setSnapshotStateAction
  | getSourcesImprovementsFlagAction
  | getIdnAPIURLAction

const getStatSuccess = ReduxHelpers.Common.Actions.setToState(
  ActionTypes.GET_STAT_SUCCESS,
  defaultState
)
type getStatSuccessAction = ReturnType<typeof getStatSuccess>

export const getProgress = (getProgressResp: GetProgressResp) =>
  ReduxHelpers.Actions.createPayloadAction(ActionTypes.GET_PROGRESS_SUCCESS, {
    getProgressResp
  })
type getProgressAction = ReturnType<typeof getProgress>

export const setSnapshotState = (snapshotState: any) =>
  ReduxHelpers.Actions.createPayloadAction(ActionTypes.SET_SNAPSHOT_STATE, {
    snapshotState
  })
type setSnapshotStateAction = ReturnType<typeof setSnapshotState>

export const getTenantSuccess = (
  tenant: TenantInfo,
  singleAccounts?: SingleAccounts,
  organizations?: Organizations
) =>
  ReduxHelpers.Actions.createPayloadAction(ActionTypes.GET_TENANT_SUCCESS, {
    tenant,
    singleAccounts,
    organizations
  })
type getTenantSuccessAction = ReturnType<typeof getTenantSuccess>

export const getUserSuccess = (user: UserInfo) =>
  ReduxHelpers.Actions.createPayloadAction(ActionTypes.GET_USER_SUCCESS, {
    user
  })
type getUserSuccessAction = ReturnType<typeof getUserSuccess>

export const getNotificationInfoSuccess = (info: NotificationInfo[]) =>
  ReduxHelpers.Actions.createPayloadAction(
    ActionTypes.GET_NOTIFICATIONS_SUCCESS,
    {
      info
    }
  )
type getNotificationSuccessAction = ReturnType<
  typeof getNotificationInfoSuccess
>

export const serverErrors = (errors: any) =>
  ReduxHelpers.Actions.createPayloadAction(ActionTypes.SERVER_ERRORS, {
    errors,
    isPending: false
  })
type serverErrorsAction = ReturnType<typeof serverErrors>

export const signOutSuccess = (isLoggedOut: boolean) =>
  ReduxHelpers.Actions.createPayloadAction(ActionTypes.SIGN_OUT_SUCCESS, {
    isLoggedOut
  })
type signOutSuccessAction = ReturnType<typeof signOutSuccess>

export const requestPending = (isPending: boolean) =>
  ReduxHelpers.Actions.createPayloadAction(ActionTypes.REQUEST_PENDING, {
    isPending
  })
type requestPendingAction = ReturnType<typeof requestPending>

export const requestPendingProgress = (isPending: boolean) =>
  ReduxHelpers.Actions.createPayloadAction(
    ActionTypes.REQUEST_PENDING_PROGRESS,
    {
      isPending
    }
  )
type requestPendingProgressAction = ReturnType<typeof requestPendingProgress>

export const accountRegisterPending = (accountRegisterPending: boolean) =>
  ReduxHelpers.Actions.createPayloadAction(
    ActionTypes.ACCOUNT_REGISTER_PENDING,
    { accountRegisterPending }
  )
type accountRegisterPendingAction = ReturnType<typeof accountRegisterPending>

export const loggedIn = (isLoggedIn: boolean) =>
  ReduxHelpers.Actions.createPayloadAction(ActionTypes.LOGGED_IN, {
    isLoggedIn
  })
type loggedInAction = ReturnType<typeof loggedIn>

export function restoreSession(first: boolean) {
  // first retrieve user info from local storage
  const userEmail: string = storage.get('userEmail')
  return (dispatch: any, getState: () => State) => {
    dispatch(requestPending(true))
    dispatch(loggedIn(false))
    if (!isEmpty(userEmail)) {
      dispatch(
        getTenant(tenant => {
          dispatch(
            getUser(correctEmailFormat(userEmail), user => {
              if (first && pendo && !pendo.isReady()) {
                pendoInitialize({ tenant, user })
              } else {
                pendoIdentify({ tenant, user })
              }
            })
          )
          dispatch(getNotificationSettings())
        })
      )
    }
    dispatch(requestPending(false))
  }
}
export function getUser(
  email: string,
  onSuccess?: (data?: UserInfo) => void,
  onFailure?: (err: string) => void
) {
  return (dispatch: any, getState: () => State) => {
    dispatch(requestPending(true))
    tenantproto.TenantMgr.GetUser({
      params: { email: correctEmailFormat(email) },
      options: { signal: null }
    })
      .then(res => {
        storage.set('userEmail', res?.user?.email)
        if (!isEmpty(res.error)) {
          toastError(jsonify(res.error))
          const errors = [{ message: res.error }]
          dispatch(serverErrors(errors))
          if (typeof onFailure === 'function') {
            onFailure(res.error)
          }
        } else {
          dispatch(getUserSuccess(res.user))
          if (onSuccess) onSuccess(res.user)
        }
        dispatch(requestPending(false))
      })
      .catch(res => {
        if (typeof onFailure === 'function') {
          onFailure(res.error)
        }
      })
  }
}

export function getNotificationSettings(
  onSuccess?: (data?: NotificationInfo[]) => void,
  onFailure?: (err: string) => void
) {
  return (dispatch: any, getState: () => State) => {
    dispatch(requestPending(true))
    tenantproto.TenantMgr.GetNotificationInfo({
      params: {},
      options: { signal: null }
    }).then(res => {
      if (!isEmpty(res.error)) {
        toastError(jsonify(res.error))
        const errors = [{ message: res.error }]
        dispatch(serverErrors(errors))
        if (typeof onFailure === 'function') {
          onFailure(res.error)
        }
      } else {
        dispatch(getNotificationInfoSuccess(res.notification_info))
        if (onSuccess) onSuccess(res.notification_info)
      }
      dispatch(requestPending(false))
    })
  }
}

export function getTenant(
  callback?: (data?: TenantInfo) => void,
  handleError?: (data?: any) => void
) {
  return (dispatch: any, getState: () => State) => {
    dispatch(requestPending(true))
    tenantproto.TenantMgr.GetTenantEndpoint({
      params: { include_organizations: true },
      options: { signal: null }
    }).then(res => {
      if (!isEmpty(res.error)) {
        if (handleError) handleError(res.error)
        else toastError(jsonify(res.error))
        dispatch(serverErrors(res.error))
        if (callback) callback(null)
        dispatch(requestPending(false))
      } else {
        const organizations = mapOrganizations(res?.tenant?.organization_info)
        const singleAccounts = mapSingleAccounts(res?.tenant?.account_info)
        dispatch(getTenantSuccess(res.tenant, singleAccounts, organizations))
        if (callback) callback(res.tenant)
      }
    })
  }
}

export function updateUserDetails(
  params: tenantproto.UpdateUserReq,
  callback: (data?: tenantproto.UpdateUserResp) => void
) {
  return (dispatch: any, getState: () => State) => {
    dispatch(requestPending(true))
    tenantproto.TenantMgr.UpdateUserEndpoint({ params }).then(res => {
      const { user, error } = res || {}
      const { auth } = getState()
      const { tenant } = auth || {}
      if (!isEmpty(error)) {
        toastError(jsonify(error))
        dispatch(serverErrors(error))
      } else {
        dispatch(getUserSuccess(user))
        if (callback) callback(res)
        pendoUpdateOptions({ user, tenant })
        dispatch(requestPending(false))
      }
    })
  }
}

export function updateNotificationSettings(
  params: tenantproto.UpdateNotificationInfoReq,
  callback: (data?: tenantproto.UpdateNotificationInfoResp) => void
) {
  return (dispatch: any, getState: () => State) => {
    dispatch(requestPending(true))
    tenantproto.TenantMgr.UpdateNotificationInfo({
      params,
      queryVars: {
        severity: params.severity
      }
    }).then(res => {
      if (!isEmpty(res.error)) {
        toastError(jsonify(res.error))
        dispatch(serverErrors(res.error))
      } else {
        dispatch(getNotificationSettings())
        if (callback) callback(res)
        dispatch(requestPending(false))
      }
    })
  }
}

function handleSnapshotErrorState(error, auth, dispatch) {
  const snapshotProgressErrorState = auth.snapshotState
  if (/Failed to get snapshot progress/.test(error)) {
    let snapshotProgressErrorStateKey =
      snapshotProgressErrorState['Failed to get snapshot progress']
    const timestamp = new Date()
    if (!snapshotProgressErrorStateKey) {
      snapshotProgressErrorStateKey = {
        lastErrorTimestamp: timestamp
      }
      dispatch(
        setSnapshotState({
          'Failed to get snapshot progress': {
            lastErrorTimestamp: timestamp,
            snapshotState: SnapshotStateEnum.Normal
          }
        })
      )
    } else if (!snapshotProgressErrorStateKey.lastErrorTimestamp) {
      snapshotProgressErrorStateKey.lastErrorTimestamp = timestamp
      dispatch(
        setSnapshotState({
          'Failed to get snapshot progress': {
            lastErrorTimestamp: timestamp,
            snapshotState: SnapshotStateEnum.Normal
          }
        })
      )
    } else {
      const minutes = differenceInMinutes(
        getToday(),
        snapshotProgressErrorStateKey?.lastErrorTimestamp
      )
      if (minutes >= 1) {
        dispatch(
          setSnapshotState({
            'Failed to get snapshot progress': {
              lastErrorTimestamp:
                snapshotProgressErrorStateKey?.lastErrorTimestamp,
              snapshotState: SnapshotStateEnum.Polling
            }
          })
        )
      }
    }
  }
}

export function getOnboardingProgress(
  params: GetProgressReq,
  callback?: (data?: OnboardingProgress[]) => void
) {
  return (dispatch: any, getState: () => State) => {
    const { organization } = getState()
    const { editor } = organization
    const openedEditor = !isEmpty(editor?.model)
    if (!openedEditor) {
      dispatch(requestPendingProgress(true))
      Calendar.GetProgress({
        params,
        makeRequestOptions: {
          pendingStatus: false,
          handleResponseErrors: response => {
            if (!response.ok) {
              return response
                .text()
                .then(text => {
                  throw new Error(text)
                })
                .catch(error => {
                  handleSnapshotErrorState(error, getState().auth, dispatch)
                })
            } else {
              dispatch(
                setSnapshotState({
                  'Failed to get snapshot progress': {
                    lastErrorTimestamp: null,
                    snapshotState: SnapshotStateEnum.Normal
                  }
                })
              )
              return response.json()
            }
          }
        }
      })
        .then(res => {
          if (callback) callback([])
          return defaultHandleStatus(
            res,
            () => {
              dispatch(requestPendingProgress(false))
              dispatch(serverErrors(res))
            },
            true
          )
        })
        .then(res => {
          dispatch(getProgress(res || {}))
        })
        .catch(ex => {
          dispatch(requestPendingProgress(false))
          return ex
        })
    }
  }
}

export function signIn() {
  return (dispatch: any, getState: () => State) => {
    dispatch(loggedIn(true))
    dispatch(requestPending(false))
  }
}

export function signOut() {
  return (dispatch: any, getState: () => State) => {
    const oauthToken = storage.get('oauthToken')
    if (oauthToken) {
      const authService = new AuthService()
      authService.logoutIdn()
    }
    dispatch(signOutSuccess(true))
    storage.purge()
    dispatch(requestPending(false))
  }
}

export const getSourcesImprovementsFlag = (status: boolean) =>
  ReduxHelpers.Actions.createPayloadAction(
    ActionTypes.SET_SOURCES_IMPROVEMENTS_FF,
    {
      sourcesImprovementsFlag: status
    }
  )
type getSourcesImprovementsFlagAction = ReturnType<
  typeof getSourcesImprovementsFlag
>

export const fetchSourcesImprovementsFlag =
  () => (dispatch: any, getStore: () => State) => {
    getFeatureFlagAsync('CAM_TEMP_20220209_SOURCES_IMPROVEMENTS', {
      signal: null
    }).then(res => {
      dispatch(getSourcesImprovementsFlag(res?.value ?? false))
    })
  }

export const idnAPIURL = (idnAPIUrl: string) =>
  ReduxHelpers.Actions.createPayloadAction(ActionTypes.SET_IDN_API_URL, {
    idnAPIUrl
  })

type getIdnAPIURLAction = ReturnType<typeof idnAPIURL>
